import {
  enqueueAtomicRpcV1,
  type AtomicRpcHandle,
} from '../sync/atomicRpcOutbox.js';

export const FOOD_SUPPLIER_MATCH_ARCHIVE_SCHEMA =
  'trackeat.food-supplier-match-archive' as const;
export const FOOD_SUPPLIER_MATCH_ARCHIVE_RESULT_SCHEMA =
  'trackeat.food-supplier-match-archive-result' as const;

export interface FoodSupplierMatchArchiveRequestV1 extends Record<string, unknown> {
  schema: typeof FOOD_SUPPLIER_MATCH_ARCHIVE_SCHEMA;
  version: 1;
  request_id: string;
  tenant_id: string;
  business_id: string;
  food_id: string;
  target_food_id: string;
  expected_version: number;
  requested_at: string;
}

export interface QueueFoodSupplierMatchArchiveInputV1 {
  requestId: string;
  tenantId: string;
  businessId: string;
  foodId: string;
  targetFoodId: string;
  expectedVersion: number;
  requestedAt: string;
}

function requireUuid(value: string, field: string): void {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    throw new Error(`food_supplier_match_archive_invalid_${field}`);
  }
}

export async function queueFoodSupplierMatchArchiveV1(
  input: QueueFoodSupplierMatchArchiveInputV1,
): Promise<AtomicRpcHandle> {
  requireUuid(input.requestId, 'request_id');
  requireUuid(input.tenantId, 'tenant_id');
  requireUuid(input.businessId, 'business_id');
  requireUuid(input.foodId, 'food_id');
  requireUuid(input.targetFoodId, 'target_food_id');
  if (input.foodId === input.targetFoodId) {
    throw new Error('food_supplier_match_archive_target_matches_source');
  }
  if (!Number.isSafeInteger(input.expectedVersion) || input.expectedVersion < 0) {
    throw new Error('food_supplier_match_archive_invalid_version');
  }
  if (!Number.isFinite(Date.parse(input.requestedAt))) {
    throw new Error('food_supplier_match_archive_invalid_requested_at');
  }
  const request: FoodSupplierMatchArchiveRequestV1 = {
    schema: FOOD_SUPPLIER_MATCH_ARCHIVE_SCHEMA,
    version: 1,
    request_id: input.requestId,
    tenant_id: input.tenantId,
    business_id: input.businessId,
    food_id: input.foodId,
    target_food_id: input.targetFoodId,
    expected_version: input.expectedVersion,
    requested_at: input.requestedAt,
  };
  const queued = await enqueueAtomicRpcV1({
    kind: 'food_supplier_match_archive_v1',
    tenantId: input.tenantId,
    businessId: input.businessId,
    scopeKey: `food-supplier-match-archive:${input.foodId}`,
    request,
    optimisticMasters: [{
      table: 'foods',
      id: input.foodId,
      op: 'archive',
      base_version: input.expectedVersion,
      patch: {},
    }],
    now: input.requestedAt,
  });
  return queued.handle;
}

export function parseFoodSupplierMatchArchiveResultV1(
  value: unknown,
  request: FoodSupplierMatchArchiveRequestV1,
  requestDigest: string,
): void {
  if (!value || typeof value !== 'object') {
    throw new Error('food_supplier_match_archive_invalid_receipt');
  }
  const result = value as Record<string, unknown>;
  if (
    result.schema !== FOOD_SUPPLIER_MATCH_ARCHIVE_RESULT_SCHEMA
    || result.version !== 1
    || result.request_id !== request.request_id
    || result.request_digest !== requestDigest
    || result.tenant_id !== request.tenant_id
    || result.food_id !== request.food_id
    || result.target_food_id !== request.target_food_id
    || typeof result.supplier_match_count !== 'number'
    || typeof result.replayed !== 'boolean'
  ) {
    throw new Error('food_supplier_match_archive_receipt_mismatch');
  }
}
